import React from "react";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import requests from "../Auth/Requests";
import {
  Box,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

export default class MachineSpecs extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      specs: [],
      loading: false,
      error: null,
      addDialogOpen: false,
      editDialogOpen: false,
      newSpec: { plan: "", maxServers: "", matchesPerServer: "" },
      editSpec: null,
    };
  }

  componentDidMount() {
    this.listSpecs();
  }

  performAction(description, ...args) {
    let payload = {};
    if (typeof args[0] === "object" && args[0] !== null) {
      payload = args[0];
      args = args.slice(1);
    }
    const cmd = args.join(" ");

    const key = enqueueSnackbar(`${description}...`, {
      variant: "info",
      persist: true,
    });

    return requests
      .request("servers machine-specs " + cmd, payload)
      .then((response) => {
        closeSnackbar(key);
        enqueueSnackbar(`${description} successful!`, {
          variant: "success",
        });
        return response;
      })
      .catch((error) => {
        closeSnackbar(key);
        enqueueSnackbar(`${description} failed: ${error.message}`, {
          variant: "error",
        });
      });
  }

  listSpecs = () => {
    this.setState({ loading: true, error: null });
    this.performAction("Listing machine specs", "list")
      .then((specs) => {
        this.setState({ specs: specs || [], loading: false });
      })
      .catch((error) => {
        this.setState({ error: error.message, loading: false });
      });
  };

  handleAddOpen = () => {
    this.setState({
      addDialogOpen: true,
      newSpec: { plan: "", maxServers: "", matchesPerServer: "" },
    });
  };

  handleAddClose = () => {
    this.setState({ addDialogOpen: false });
  };

  handleEditOpen = (spec) => {
    this.setState({
      editDialogOpen: true,
      editSpec: { ...spec },
    });
  };

  handleEditClose = () => {
    this.setState({ editDialogOpen: false, editSpec: null });
  };

  handleFieldChange = (event, type) => {
    const { name, value } = event.target;
    if (type === "new") {
      this.setState((prev) => ({
        newSpec: { ...prev.newSpec, [name]: name === "maxServers" || name === "matchesPerServer" ? Number(value) : value },
      }));
    } else if (type === "edit") {
      this.setState((prev) => ({
        editSpec: { ...prev.editSpec, [name]: name === "maxServers" || name === "matchesPerServer" ? Number(value) : value },
      }));
    }
  };

  handleAddSpec = () => {
    const { newSpec } = this.state;
    this.performAction("Adding machine spec", newSpec, "add")
      .then((specs) => {
        this.setState({ specs: specs || [], addDialogOpen: false });
      })
      .catch(() => {});
  };

  handleEditSpec = () => {
    const { editSpec } = this.state;
    if (!editSpec || !editSpec.id) return;
    this.performAction("Updating machine spec", editSpec, "update")
      .then((specs) => {
        this.setState({ specs: specs || [], editDialogOpen: false, editSpec: null });
      })
      .catch(() => {});
  };

  handleRemoveSpec = (specId) => {
    this.performAction("Removing machine spec", "remove", specId)
      .then((specs) => {
        this.setState({ specs: specs || [] });
      })
      .catch(() => {});
  };

  render() {
    const {
      specs,
      loading,
      error,
      addDialogOpen,
      editDialogOpen,
      newSpec,
      editSpec,
    } = this.state;
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <h2>Machine Specifications</h2>
          <Button variant="contained" color="primary" onClick={this.handleAddOpen}>
            Add Spec
          </Button>
        </Box>
        {loading && <div>Loading...</div>}
        {error && <div style={{ color: "red" }}>{error}</div>}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell>Max Servers</TableCell>
                <TableCell>Matches Per Server</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {specs.map((spec) => (
                <TableRow key={spec.id || spec.plan}>
                  <TableCell>{spec.plan}</TableCell>
                  <TableCell>{spec.maxServers}</TableCell>
                  <TableCell>{spec.matchesPerServer}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => this.handleEditOpen(spec)}
                      sx={{ mr: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => this.handleRemoveSpec(spec.id)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Dialog */}
        <Dialog open={addDialogOpen} onClose={this.handleAddClose}>
          <DialogTitle>Add Machine Spec</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Plan"
              name="plan"
              value={newSpec.plan}
              onChange={(e) => this.handleFieldChange(e, "new")}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Max Servers"
              name="maxServers"
              type="number"
              value={newSpec.maxServers}
              onChange={(e) => this.handleFieldChange(e, "new")}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Matches Per Server"
              name="matchesPerServer"
              type="number"
              value={newSpec.matchesPerServer}
              onChange={(e) => this.handleFieldChange(e, "new")}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleAddClose}>Cancel</Button>
            <Button onClick={this.handleAddSpec} variant="contained">
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={this.handleEditClose}>
          <DialogTitle>Edit Machine Spec</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Plan"
              name="plan"
              value={editSpec ? editSpec.plan : ""}
              onChange={(e) => this.handleFieldChange(e, "edit")}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Max Servers"
              name="maxServers"
              type="number"
              value={editSpec ? editSpec.maxServers : ""}
              onChange={(e) => this.handleFieldChange(e, "edit")}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Matches Per Server"
              name="matchesPerServer"
              type="number"
              value={editSpec ? editSpec.matchesPerServer : ""}
              onChange={(e) => this.handleFieldChange(e, "edit")}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleEditClose}>Cancel</Button>
            <Button onClick={this.handleEditSpec} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}